<script setup>
import { ref } from 'vue'
import { useDirectoryStore } from '../stores/directory'
import { NodeType } from '../types'
import TreeNode from './TreeNode.vue'

const props = defineProps({
  workspaceId: { type: String, required: true }
})

const emit = defineEmits(['open-document', 'send-to-canvas'])

const directoryStore = useDirectoryStore()

// 右键菜单
const contextMenu = ref({ visible: false, x: 0, y: 0, node: null })
const renameInput = ref({ visible: false, nodeId: null, value: '' })
const newNodeInput = ref({ visible: false, parentId: null, type: NodeType.FOLDER, value: '' })

const handleNodeClick = (node) => {
  directoryStore.selectNode(node.node_id)
  if (node.type === NodeType.FOLDER) {
    directoryStore.toggleExpand(node.node_id)
  } else if (node.type === NodeType.DOCUMENT) {
    emit('open-document', node)
  }
}

const handleContextMenu = (e, node) => {
  e.preventDefault()
  contextMenu.value = { visible: true, x: e.clientX, y: e.clientY, node }
}

const closeContextMenu = () => {
  contextMenu.value.visible = false
}

const startNewNode = (parentId, type) => {
  closeContextMenu()
  newNodeInput.value = { visible: true, parentId, type, value: '' }
  if (parentId) directoryStore.expandToNode(parentId)
}

const confirmNewNode = async () => {
  const { parentId, type, value } = newNodeInput.value
  if (!value.trim()) {
    newNodeInput.value.visible = false
    return
  }
  await directoryStore.createNode(props.workspaceId, parentId, value.trim(), type)
  newNodeInput.value = { visible: false, parentId: null, type: NodeType.FOLDER, value: '' }
}

const cancelNewNode = () => {
  newNodeInput.value = { visible: false, parentId: null, type: NodeType.FOLDER, value: '' }
}

const startRename = (node) => {
  closeContextMenu()
  renameInput.value = { visible: true, nodeId: node.node_id, value: node.name }
}

const confirmRename = async () => {
  if (renameInput.value.value.trim()) {
    await directoryStore.renameNode(renameInput.value.nodeId, renameInput.value.value.trim())
  }
  renameInput.value = { visible: false, nodeId: null, value: '' }
}

const cancelRename = () => {
  renameInput.value = { visible: false, nodeId: null, value: '' }
}

const deleteNode = async (node) => {
  closeContextMenu()
  await directoryStore.deleteNode(node.node_id)
}

const sendToCanvas = (node) => {
  closeContextMenu()
  emit('send-to-canvas', node)
}

// 新建输入值更新
const handleNewNodeValueUpdate = (val) => {
  newNodeInput.value.value = val
}

// 重命名输入值更新
const handleRenameValueUpdate = (val) => {
  renameInput.value.value = val
}
</script>

<template>
  <div class="directory-panel" @click="closeContextMenu">
    <!-- 顶部操作栏 -->
    <div class="panel-toolbar">
      <span class="panel-title">📁 目录</span>
      <div class="toolbar-actions">
        <button class="tool-btn" title="新建文件夹" @click.stop="startNewNode(null, 'folder')">📁+</button>
        <button class="tool-btn" title="新建文档" @click.stop="startNewNode(null, 'document')">📄+</button>
      </div>
    </div>

    <!-- 树形目录 -->
    <div class="tree-container">
      <template v-if="directoryStore.treeData.length === 0 && !newNodeInput.visible">
        <div class="empty-hint">暂无内容，点击上方按钮创建</div>
      </template>

      <div class="tree-list">
        <TreeNode
          v-for="node in directoryStore.treeData"
          :key="node.node_id"
          :node="node"
          :depth="0"
          :selected-node-id="directoryStore.selectedNodeId"
          :rename-node-id="renameInput.visible ? renameInput.nodeId : null"
          :rename-value="renameInput.value"
          :new-node-input="newNodeInput"
          @node-click="handleNodeClick"
          @context-menu="handleContextMenu"
          @rename-update="handleRenameValueUpdate"
          @rename-confirm="confirmRename"
          @rename-cancel="cancelRename"
          @new-node-update="handleNewNodeValueUpdate"
          @new-node-confirm="confirmNewNode"
          @new-node-cancel="cancelNewNode"
        />

        <!-- 根级别的新建节点输入 -->
        <div v-if="newNodeInput.visible && newNodeInput.parentId === null" class="new-node-input">
          <span class="node-icon">{{ newNodeInput.type === 'folder' ? '📁' : '📄' }}</span>
          <input
            v-model="newNodeInput.value"
            :placeholder="newNodeInput.type === 'folder' ? '文件夹名称' : '文档名称'"
            class="rename-input"
            @keyup.enter="confirmNewNode"
            @keyup.escape="cancelNewNode"
            @blur="confirmNewNode"
            autofocus
          />
        </div>
      </div>
    </div>

    <!-- 右键菜单 -->
    <Teleport to="body">
      <div
        v-if="contextMenu.visible"
        class="context-menu"
        :style="{ left: contextMenu.x + 'px', top: contextMenu.y + 'px' }"
        @click.stop
      >
        <div class="menu-item" @click="handleNodeClick(contextMenu.node)">打开</div>
        <div class="menu-item" @click="sendToCanvas(contextMenu.node)">发送到画布</div>
        <div class="menu-divider" />
        <template v-if="contextMenu.node?.type === 'folder'">
          <div class="menu-item" @click="startNewNode(contextMenu.node.node_id, 'folder')">新建文件夹</div>
          <div class="menu-item" @click="startNewNode(contextMenu.node.node_id, 'document')">新建文档</div>
          <div class="menu-divider" />
        </template>
        <div class="menu-item" @click="startRename(contextMenu.node)">重命名</div>
        <div class="menu-item danger" @click="deleteNode(contextMenu.node)">删除</div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.directory-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  font-size: 13px;
}

.panel-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  flex-shrink: 0;
}

.panel-title {
  font-weight: 600;
  color: #374151;
}

.toolbar-actions {
  display: flex;
  gap: 4px;
}

.tool-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 14px;
  padding: 2px 6px;
  border-radius: 4px;
  transition: background 0.15s;
}

.tool-btn:hover {
  background: #e5e7eb;
}

.tree-container {
  flex: 1;
  overflow-y: auto;
  padding: 4px 0;
}

.empty-hint {
  padding: 20px;
  text-align: center;
  color: #9ca3af;
  font-size: 12px;
}

.new-node-input {
  display: flex;
  align-items: center;
  padding: 4px 10px;
  margin: 1px 4px;
}

.node-icon {
  margin-right: 6px;
  font-size: 14px;
  flex-shrink: 0;
}

.rename-input {
  width: 100%;
  border: 1px solid #0ea5e9;
  border-radius: 3px;
  padding: 2px 6px;
  font-size: 13px;
  outline: none;
}

.rename-input:focus {
  box-shadow: 0 0 0 2px rgba(14, 165, 233, 0.15);
}

/* 右键菜单 */
.context-menu {
  position: fixed;
  z-index: 10000;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  padding: 4px 0;
  min-width: 160px;
}

.menu-item {
  padding: 6px 16px;
  cursor: pointer;
  transition: background 0.12s;
  font-size: 13px;
}

.menu-item:hover {
  background: #f3f4f6;
}

.menu-item.danger {
  color: #ef4444;
}

.menu-item.danger:hover {
  background: #fef2f2;
}

.menu-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 4px 0;
}
</style>
